import type { FC } from 'react';
import { useSelector } from 'react-redux';
import Head from 'next/head';

import type { ImageWrapper } from 'src/__generated__/graphqlTypes';
import type { Store } from 'app-redux/types/storeTypes';
import { WebpImage } from 'components/Shared/SharedComponents';
import { ViewerDevice } from 'constants/enums';
import type { WebpWidthLimitType } from 'types/objectTypes';
import type { IFullBackground } from 'types/componentTypes';
import { setItemInObjectBySlug } from 'lib/sharedMethods.service';
import { IS_CLIENT_SIDE } from 'constants/constants';

const backgroundSlugs = {
  mobile: ViewerDevice.MOBILE,
  desktop: ViewerDevice.DESKTOP,
  tablet: ViewerDevice.TABLET,
};

const getIconWidthLimit = (
  width?: number,
  media?: string | number,
  rest?: Partial<WebpWidthLimitType>,
) => {
  if (!width) {
    return {} as WebpWidthLimitType;
  }

  return ({
    width,
    media: media ? `${media}px` : undefined,
    ...rest,
  });
};

const FullBackgroundImage: FC<IFullBackground> = ({
  backgrounds,
  isHighPriority,
  widths,
  skipTablet,
  skipMobile,
  ...rest
}) => {
  const viewerDevice = useSelector((store: Store) => store.server.app.viewerDevice);
  const {
    mobileViewMaxWidth,
    commonTabletMaxWidth,
  } = useSelector((store: Store) => store.server.app.scssVariables);

  if (!backgrounds || !backgrounds.length) {
    return null;
  }

  const reducer = setItemInObjectBySlug(backgroundSlugs);
  const sortedBackgrounds = backgrounds
    .reduce(reducer, {} as Record<ViewerDevice, ImageWrapper>);
  const background = sortedBackgrounds[viewerDevice] || sortedBackgrounds.desktop;
  const { image } = background || {};

  if (!image) {
    return null;
  }

  const widthLimits: Array<WebpWidthLimitType> = [];
  const desktopImageWidth = widths?.desktop || image.width! || +commonTabletMaxWidth;
  const desktopMedia = getIconWidthLimit(
    desktopImageWidth,
    commonTabletMaxWidth,
    { isMinWidth: true },
  );
  widthLimits.push(desktopMedia);

  if (!skipTablet) {
    const tabletImageWidth = widths?.tablet || image.width! || +commonTabletMaxWidth;
    const tabletMedia = getIconWidthLimit(
      tabletImageWidth,
      commonTabletMaxWidth,
    );
    widthLimits.push(tabletMedia);
  }

  if (!skipMobile) {
    const mobileImageWidth = widths?.mobile || (image.width)
      ? Math.round(image.width! / 2)
      : +mobileViewMaxWidth!;
    const mobileMedia = getIconWidthLimit(
      mobileImageWidth,
      mobileViewMaxWidth,
    );
    widthLimits.push(mobileMedia);
  }

  /**
     * Preload of the image with Head doesn't work because
     * {@link WebpImage} loads the image with some additional
     * parameters that makes preload of predefined image url
     * useless
     * */
  return (
    <>
      {image.url && isHighPriority && (
        <Head>
          {!IS_CLIENT_SIDE && widthLimits.map((widthLimit) => {
            const highUrl = `${image.url}?fm=webp&w=${widthLimit.width * 2}`;
            const lowUrl = `${image.url}?fm=webp&w=${widthLimit.width}`;

            return (
              <>
                <link
                  key={highUrl}
                  data-density="high"
                  rel="preload"
                  href={highUrl}
                  as="image"
                  // @ts-ignore
                  fetchpriority="high"
                  type="image/webp"
                  media={`(${widthLimit.isMinWidth ? 'min' : 'max'}-width: ${widthLimit.media})`}
                />
                <link
                  key={lowUrl}
                  data-density="low"
                  rel="preload"
                  href={lowUrl}
                  as="image"
                  // @ts-ignore
                  fetchpriority="high"
                  type="image/webp"
                  media={`(${widthLimit.isMinWidth ? 'min' : 'max'}-width: ${widthLimit.media})`}
                />
              </>

            );
          })}

          <script dangerouslySetInnerHTML={{ __html: `
            (function () {
              const urls = ${JSON.stringify(widthLimits.map((widthLimit) => ({ low: `${image.url}?fm=webp&w=${widthLimit.width}`, high: `${image.url}?fm=webp&w=${widthLimit.width * 2}`, media: `(${widthLimit.isMinWidth ? 'min' : 'max'}-width: ${widthLimit.media})` })))};
              const checkIsHighDensity = () => window?.devicePixelRatio > 1;
              const isHighDensity = checkIsHighDensity();
              const links = document.head.querySelectorAll("link[data-density]");
              links.forEach((link) => {
                if(isHighDensity && link.dataset.density === "low") {
                  link.remove();
                } 
                if(!isHighDensity && link.dataset.density === "high") { 
                  link.remove();
                }
              });
            })();
          ` }}
          />
        </Head>
      )}

      <WebpImage
        image={image}
        disableDefaultSource={rest.disableDefaultSource}
        {...(isHighPriority && { fetchpriority: 'high' })}
        widthLimit={widthLimits}
        {...rest}
      />
    </>

  );
};

export default FullBackgroundImage;
